/**
 * Azure Application Insights Integration - Logging Only
 */

let appInsights = null
let initPromise = null

const currentPageViewId = ""

const enableCorsCorrelation =
  process.env.NEXT_PUBLIC_APPLICATIONINSIGHTS_ENABLE_CORS_CORRELATION === "true"

/**
 * Auto route tracking is on unless explicitly turned off, so page views are
 * collected without per-environment configuration.
 *
 * Only "false", "off" and "0" disable it. The superseded
 * NEXT_PUBLIC_APPLICATIONINSIGHTS_TRACKING variable and its enabled/disabled
 * convention are deliberately not read: an environment still set to "disabled"
 * gets tracking, so those pipelines must move to the name below with "false".
 *
 * @param {string} value raw env value.
 * @return {boolean} whether the SDK should track route changes.
 */
export const isAutoRouteTrackingEnabled = value =>
  !["false", "off", "0"].includes(String(value ?? "").toLowerCase())

// Named for the SDK option it sets: it hooks history and refreshes the
// operation-correlation context, so it governs more than page views.
// NEXT_PUBLIC_* is inlined at build, so this must stay a literal member
// expression; a dynamic lookup evaluates to undefined in the browser bundle.
const enableAutoRouteTracking = isAutoRouteTrackingEnabled(
  process.env.NEXT_PUBLIC_APPINSIGHTS_AUTO_ROUTE_TRACKING
)

/**
 * The SDK hooks unload, beforeunload and pagehide together. Registering
 * unload makes the page ineligible for the back/forward cache in Chrome and
 * Safari, which costs users instant back-navigation restores across the site.
 * Excluding it leaves pagehide, which fires reliably and still flushes.
 * Set the flag to "true" to restore the SDK's legacy unload hooks.
 */
const enablePageUnloadEvents =
  process.env.NEXT_PUBLIC_APPLICATIONINSIGHTS_ENABLE_PAGE_UNLOAD_EVENTS ===
  "true"

// Helper function to safely access window object
const getWindow = () => {
  if (typeof window === "undefined") {
    return null
  }
  return window
}

/**
 * Initialize Azure Application Insights.
 *
 * The SDK is ~58KB gzipped, so it is imported dynamically to keep it out of
 * the main bundle: pages that never enable telemetry no longer pay its
 * download and parse cost. web-vitals observers use the `buffered` flag, so
 * starting the capture after this resolves still back-fills earlier entries.
 *
 * @param {string} connectionString - The Application Insights connection string
 * @return {Promise<object|null>} - The initialized instance, or null on failure
 */
export function initAppInsights(connectionString) {
  if (appInsights) {
    return Promise.resolve(appInsights)
  }

  // Collapse concurrent callers onto one import and one instance.
  if (initPromise) {
    return initPromise
  }

  if (!connectionString) {
    console.error("Application Insights: Connection string is required")
    return Promise.resolve(null)
  }

  if (!getWindow()) {
    return Promise.resolve(null)
  }

  initPromise = (async () => {
    try {
      const { ApplicationInsights } = await import(
        "@microsoft/applicationinsights-web"
      )

      const instance = new ApplicationInsights({
        config: {
          connectionString: connectionString,
          enableAutoRouteTracking,
          enableCorsCorrelation,
          enableRequestHeaderTracking: false,
          enableResponseHeaderTracking: false,
          disableFetchTracking: false,
          enableAjaxPerfTracking: false,
          maxBatchInterval: 15000,
          disableFlushOnBeforeUnload: false,
          enableDebug: false,
          autoTrackPageVisitTime: false,
          ...(enablePageUnloadEvents
            ? {}
            : { disablePageUnloadEvents: ["unload", "beforeunload"] }),
        },
      })

      // Initialize the SDK
      instance.loadAppInsights()
      appInsights = instance

      const windowObject = getWindow()
      if (windowObject && enablePageUnloadEvents) {
        // Redundant with disableFlushOnBeforeUnload: false, so it is kept only
        // when the legacy unload hooks are explicitly re-enabled.
        windowObject.addEventListener("beforeunload", () => {
          instance.flush()
        })
      }

      return appInsights
    } catch (error) {
      console.error("Application Insights: Initialization failed", error)
      // Allow a later caller to retry rather than caching the failure.
      initPromise = null
      return null
    }
  })()

  return initPromise
}

/**
 * Get the Application Insights instance.
 * Stays synchronous and returns null until initAppInsights resolves; callers
 * are expected to tolerate that (the Web Vitals capture queues and retries).
 * @return {object|null}
 */
export function getAppInsights() {
  return appInsights
}

/**
 * Get current page view ID for correlation
 * @return {string}
 */
export function getCurrentPageViewId() {
  return currentPageViewId
}
